import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusCircleFilled,
  ReloadOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons';
import { getDiscountStatus } from '@mahawi/eshop-common';
import {
  EDiscountAssignmentType,
  EDiscountStatus,
  EDiscountType,
  type IDiscount,
} from '@mahawi/eshop-common/src/types';
import { Button, Divider, Popconfirm, Space, Switch, Tag } from 'antd';
import { type ColumnsType } from 'antd/es/table';
import { type TableRowSelection } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import DetailPageHeader from '../components/detail-page-header';
import DiscountAssignModal from '../components/discount/discount-assign-modal';
import DiscountForm from '../components/discount/discount-form';
import DiscountRedemptionsDrawer from '../components/discount/discount-redemptions-drawer';
import Link from '../components/link';
import TableComponent from '../components/table';
import UpdatedAt from '../components/updated-at';
import { EDateFormat } from '../fragments/date-format';
import { EMPTY_VALUE } from '../fragments/empty-value';
import { getColumnSearch } from '../fragments/table-get-column-search';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import {
  discountDelete,
  discountSetEnabled,
  discountsLoad,
  discountTypesLoad,
} from '../reducers/discount/reducer';
import { SPACING } from '../theme/tokens';

const STATUS_COLOR: Record<EDiscountStatus, string> = {
  [EDiscountStatus.UPCOMING]: 'blue',
  [EDiscountStatus.ACTIVE]: 'green',
  [EDiscountStatus.EXPIRED]: 'default',
  [EDiscountStatus.EXHAUSTED]: 'orange',
  [EDiscountStatus.DISABLED]: 'red',
};

type TDiscountRow = IDiscount & { key: string; status: EDiscountStatus };

const getDiscountTypeCode = (
  discountTypeUUID: string,
  discountTypes: { uuid: string; code: string }[] | undefined,
): string =>
  discountTypes?.find((dt) => dt.uuid === discountTypeUUID)?.code ||
  EMPTY_VALUE;

function DiscountContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const discountDiscounts = useSelector((s: RootState) => s.Discount.discounts);
  const discountDiscountTypes = useSelector(
    (s: RootState) => s.Discount.discountTypes,
  );
  const discountInProcess = useSelector((s: RootState) => s.Discount.inProcess);
  const discountUpdatedAt = useSelector((s: RootState) => s.Discount.updatedAt);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState<IDiscount | null>(
    null,
  );
  const [usageDiscount, setUsageDiscount] = useState<IDiscount | null>(null);
  const [selectedDiscountUuids, setSelectedDiscountUuids] = useState<string[]>(
    [],
  );
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);

  const now: Date = useMemo(() => new Date(), [discountDiscounts]);

  const dataSource = useMemo(
    () =>
      (discountDiscounts ?? []).map((discount: IDiscount) => ({
        key: discount.uuid,
        ...discount,
        status: getDiscountStatus(discount, discount.redemptionCount, now),
      })),
    [discountDiscounts, now],
  );

  const columns = useMemo<ColumnsType<TDiscountRow>>(
    () => [
      {
        title: 'Code',
        dataIndex: 'code',
        sorter: (a: TDiscountRow, b: TDiscountRow): number =>
          a.code.localeCompare(b.code),
        ...getColumnSearch('code'),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        filters: Object.values(EDiscountStatus).map((status) => ({
          text: status,
          value: status,
        })),
        onFilter: (value: boolean | string, record: { status: string }) =>
          record.status === value,
        render: (status: EDiscountStatus): React.ReactElement => (
          <Tag color={STATUS_COLOR[status]}>{status}</Tag>
        ),
      },
      {
        title: 'Discount type',
        dataIndex: ['discountType', 'uuid'],
        render: (uuid: string): string =>
          getDiscountTypeCode(uuid, discountDiscountTypes),
      },
      {
        title: 'Value',
        dataIndex: 'value',
      },
      {
        title: 'Referral',
        key: 'referral',
        render: (
          _text: unknown,
          record: TDiscountRow,
        ): React.ReactElement | string => {
          if (record.discountType.code !== EDiscountType.REFERRAL) {
            return EMPTY_VALUE;
          }

          const reward =
            record.referrerRewardValue !== null
              ? `${record.referrerRewardValue} ${record.referrerRewardDiscountType?.code ?? ''}`.trim()
              : EMPTY_VALUE;

          return (
            <Space orientation="vertical" size={SPACING.none}>
              <span>Referrer reward: {reward}</span>
              {record.ownerCustomerUuid ? (
                <Link
                  to={`/crm/customer/${record.ownerCustomerUuid}`}
                  label="Owner"
                  openInNewTab
                />
              ) : (
                <span>Template</span>
              )}
            </Space>
          );
        },
      },
      {
        title: 'Multi-use',
        dataIndex: 'multiUse',
        render: (multiUse: boolean): string => (multiUse ? 'Yes' : 'No'),
      },
      {
        title: 'Combinable',
        dataIndex: 'combinable',
        render: (combinable: boolean): string => (combinable ? 'Yes' : 'No'),
      },
      {
        title: 'Targeting',
        dataIndex: 'assignmentType',
        filters: Object.values(EDiscountAssignmentType).map((type) => ({
          text: type,
          value: type,
        })),
        onFilter: (
          value: boolean | string,
          record: { assignmentType: string },
        ) => record.assignmentType === value,
        render: (
          assignmentType: EDiscountAssignmentType,
          record: TDiscountRow,
        ): string => {
          if (assignmentType === EDiscountAssignmentType.NONE) {
            return 'Public';
          }

          const label =
            assignmentType === EDiscountAssignmentType.INDIVIDUAL
              ? 'Individual'
              : 'Segment';

          return `${label} (${record.assignedCount} assigned)`;
        },
      },
      {
        title: 'Redemptions',
        dataIndex: 'redemptionCount',
        sorter: (a: TDiscountRow, b: TDiscountRow): number =>
          a.redemptionCount - b.redemptionCount,
        render: (count: number, record: TDiscountRow): string =>
          record.multiUse && record.maxRedemptions
            ? `${count} / ${record.maxRedemptions}`
            : `${count}`,
      },
      {
        title: 'Valid from',
        dataIndex: 'validFrom',
        sorter: (a: TDiscountRow, b: TDiscountRow): number =>
          (a.validFrom ? dayjs(a.validFrom).unix() : 0) -
          (b.validFrom ? dayjs(b.validFrom).unix() : 0),
        render: (validFrom: Date | null): string =>
          validFrom ? dayjs(validFrom).format(EDateFormat.DATE) : EMPTY_VALUE,
      },
      {
        title: 'Valid to',
        dataIndex: 'validTo',
        sorter: (a: TDiscountRow, b: TDiscountRow): number =>
          (a.validTo ? dayjs(a.validTo).unix() : Number.MAX_SAFE_INTEGER) -
          (b.validTo ? dayjs(b.validTo).unix() : Number.MAX_SAFE_INTEGER),
        render: (validTo: Date | null): string =>
          validTo ? dayjs(validTo).format(EDateFormat.DATE) : 'Never expires',
      },
      {
        title: 'Order',
        dataIndex: ['order', 'uuid'],
        render: (uuid: string | undefined): React.ReactElement | string =>
          uuid ? (
            <Link to={`/order/${uuid}`} label={uuid} openInNewTab />
          ) : (
            EMPTY_VALUE
          ),
      },
      {
        title: 'Actions',
        render: (_text: unknown, record: TDiscountRow): React.ReactElement => (
          <Space size={SPACING.sm}>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={(): void => {
                setEditingDiscount(record);
                setIsFormOpen(true);
              }}
              aria-label="Edit coupon"
            />
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={(): void => setUsageDiscount(record)}
              aria-label="View usage"
            />
            <Popconfirm
              title={
                record.enabled
                  ? 'Disable this coupon?'
                  : 'Re-enable this coupon?'
              }
              onConfirm={(): void => {
                dispatch(discountSetEnabled(record.uuid, !record.enabled));
              }}
            >
              <Switch
                size="small"
                checked={record.enabled}
                disabled={discountInProcess}
              />
            </Popconfirm>
            {record.redemptionCount === 0 && !record.order.uuid && (
              <Popconfirm
                title="Delete this coupon?"
                onConfirm={(): void => {
                  dispatch(discountDelete(record.uuid));
                }}
              >
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  aria-label="Delete coupon"
                />
              </Popconfirm>
            )}
          </Space>
        ),
      },
    ],
    [discountDiscountTypes, discountInProcess, dispatch],
  );

  const rowSelection: TableRowSelection<object> = {
    selectedRowKeys: selectedDiscountUuids,
    onChange: (selectedRowKeys): void => {
      setSelectedDiscountUuids(selectedRowKeys as string[]);
    },
    getCheckboxProps: (record: object) => {
      const isPublic: boolean =
        'assignmentType' in record &&
        record.assignmentType === EDiscountAssignmentType.NONE;

      return {
        disabled: isPublic,
        title: isPublic
          ? 'Public coupons cannot be assigned to customers'
          : undefined,
      };
    },
  };

  return (
    <Space size={SPACING.lg} orientation="vertical" style={{ width: '100%' }}>
      <DetailPageHeader
        title="Coupons"
        actions={
          <>
            <Button
              icon={<UsergroupAddOutlined />}
              disabled={selectedDiscountUuids.length === 0}
              onClick={(): void => setIsAssignModalOpen(true)}
            >
              Assign to customers
              {selectedDiscountUuids.length > 0 &&
                ` (${selectedDiscountUuids.length})`}
            </Button>
            <Button
              type="primary"
              icon={<PlusCircleFilled />}
              onClick={(): void => {
                setEditingDiscount(null);
                setIsFormOpen(true);
              }}
            >
              New coupon
            </Button>
          </>
        }
      />

      <Space
        size={SPACING.lg}
        align="center"
        separator={<Divider orientation="vertical" />}
      >
        <Button
          type={
            !discountDiscountTypes || !discountDiscounts ? 'primary' : 'default'
          }
          onClick={(): void => {
            dispatch(discountTypesLoad());
            dispatch(discountsLoad());
          }}
          disabled={discountInProcess}
        >
          Reload all <ReloadOutlined />
        </Button>

        <UpdatedAt value={discountUpdatedAt} />
      </Space>

      <TableComponent
        columns={columns as Parameters<typeof TableComponent>[0]['columns']}
        dataSource={dataSource}
        isLoading={discountInProcess && !discountDiscounts}
        emptyText="No coupons found"
        rowSelection={rowSelection}
      />

      <DiscountForm
        open={isFormOpen}
        onClose={(): void => setIsFormOpen(false)}
        editingDiscount={editingDiscount}
        discountTypes={discountDiscountTypes}
        inProcess={discountInProcess}
      />

      <DiscountRedemptionsDrawer
        open={!!usageDiscount}
        onClose={(): void => setUsageDiscount(null)}
        discountUUID={usageDiscount?.uuid ?? null}
        discountCode={usageDiscount?.code ?? null}
      />

      <DiscountAssignModal
        open={isAssignModalOpen}
        onClose={(): void => {
          setIsAssignModalOpen(false);
          setSelectedDiscountUuids([]);
        }}
        discountUuids={selectedDiscountUuids}
      />
    </Space>
  );
}

export default DiscountContainer;
